/**
 * Adresse d'affichage d'un restaurant : rue + code postal + ville.
 *
 * Il n'existait aucune valeur d'`address` correcte partout (DF-0132). Le pied de
 * ce site n'affichait que `address` brut, la vitrine concaténait
 * `address` + « code_postal ville » :
 *
 * - `address = "12 All. du 7ème art, 33400 Talence"` → la vitrine affichait la
 *   ville DEUX fois ;
 * - `address = "12 allée du 7ème Art"` (la convention des restaurants récents,
 *   et ce que le back-office écrit) → le pied d'ici perdait « 33400 Talence ».
 *
 * Les deux surfaces composent donc les trois champs de la même façon, et
 * n'ajoutent la localité que si la rue ne la porte pas déjà — sinon corriger un
 * site casserait l'autre. Miroir de `formatDisplayAddress` dans
 * `ouitap-showcase/src/lib/sections/mapEmbed.ts`.
 */

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * La rue porte-t-elle déjà sa localité ? Le code postal tranche seul quand il
 * est là : c'est un jeton non ambigu. Sans lui on ne se fie qu'à une ville en
 * fin de chaîne — « 3 rue de Paris » à Paris ne doit pas faire disparaître
 * « 75011 Paris ».
 */
function carriesLocality(
  address: string,
  postalCode: string,
  city: string,
): boolean {
  const street = normalize(address);
  if (postalCode) {
    return street.split(' ').includes(normalize(postalCode));
  }
  const c = normalize(city);
  return Boolean(c) && street.endsWith(c);
}

export interface AddressParts {
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
}

/** Adresse sur une ligne : « 12 allée du 7ème Art, 33400 Talence ». */
export function formatDisplayAddress(parts: AddressParts): string {
  const address = (parts.address ?? '').trim();
  const postalCode = (parts.postalCode ?? '').trim();
  const city = (parts.city ?? '').trim();
  const locality = [postalCode, city].filter(Boolean).join(' ').trim();

  if (!address) return locality;
  if (!locality) return address;
  if (carriesLocality(address, postalCode, city)) return address;
  return `${address}, ${locality}`;
}

/**
 * URL d'iframe Google Maps composée à partir de l'adresse seule.
 *
 * Point d'entrée public « Embed a map » de maps.google.com : aucune clé, donc
 * aucun quota Google Maps Platform consommé — contrairement à l'API Embed
 * (`/maps/embed/v1/place?key=…`) et à l'API JavaScript, qui facturent chaque
 * chargement. Miroir de `buildMapEmbedUrl()` dans
 * `ouitap-showcase/src/lib/sections/mapEmbed.ts`.
 *
 * `hl` suit la langue du site : les libellés de la carte sont traduits sans
 * changer de source de données.
 */
export function buildMapEmbedUrl(address: string, lang: string): string {
  const query = address.trim();
  if (!query) return '';
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed&hl=${encodeURIComponent(lang)}`;
}
